import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { requestJSON } from "../api";
import { useI18n } from "../i18n";
import {
  ManagementDialog,
  ManagementPanel,
  ManagementPagination,
} from "../managementUI";
import {
  date,
  duration,
  fresh,
  stateLabel,
  type ExecutionRecord,
  type ExecutionSnapshot,
  type MonitoringData,
} from "./model";
import { Empty, Note, Summary, Table } from "./ui";
import { PersistentSandboxExecution } from "./PersistentSandboxExecution";

export function ExecutionMonitoring({
  data,
  now,
  refreshKey,
}: {
  data: MonitoringData;
  now: number;
  refreshKey: number;
}) {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const source = params.get("execution_source") || "sandbox";
  const requestedHours = Number(params.get("hours") ?? 24);
  const hours = [0, 1, 24, 168].includes(requestedHours) ? requestedHours : 24;
  const filter = params.get("result") || "all";
  const search = params.get("search") || "";
  const [snapshot, setSnapshot] = useState<ExecutionSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<ExecutionRecord | null>(null);
  const supported = ["registrations", "operations", "publish"].includes(source);
  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next, { replace: true });
    setOffset(0);
  };
  useEffect(() => {
    if (!supported) {
      setSnapshot(null);
      setLoading(false);
      return;
    }
    let controller: AbortController;
    const load = async () => {
      controller?.abort();
      const active = new AbortController();
      controller = active;
      setLoading(true);
      try {
        const result = await requestJSON<ExecutionSnapshot>(
          `/api/v1/monitoring/executions?source=${encodeURIComponent(source)}`,
          { signal: active.signal },
        );
        if (!active.signal.aborted) {
          setSnapshot(result);
          setFailed(false);
        }
      } catch {
        if (!active.signal.aborted) {
          setFailed(true);
          setSnapshot(null);
        }
      } finally {
        if (!active.signal.aborted) setLoading(false);
      }
    };
    setSnapshot(null);
    void load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, 30_000);
    return () => {
      controller?.abort();
      window.clearInterval(timer);
    };
  }, [source, supported, refreshKey]);
  const usable =
    !!snapshot?.available &&
    !failed &&
    now - Date.parse(snapshot.observed_at) < 90_000;
  const records = usable ? (snapshot?.items ?? []) : [];
  const successes = new Set(["succeeded", "success", "completed", "ready"]);
  const failures = new Set(["failed", "timeout", "timed_out"]);
  const inProgress = new Set([
    "running",
    "pending",
    "queued",
    "requested",
    "accepted",
    "provisioning",
  ]);
  const live = records.filter((r) => inProgress.has(r.state.toLowerCase()));
  const recent = records.filter(
    (r) => hours === 0 || Date.parse(r.created_at) >= now - hours * 3600_000,
  );
  const succeeded = recent.filter((r) =>
    successes.has(r.state.toLowerCase()),
  ).length;
  const errored = recent.filter((r) =>
    failures.has(r.state.toLowerCase()),
  ).length;
  const items = recent.filter(
    (r) =>
      (filter === "all" ||
        (filter === "errors" && failures.has(r.state.toLowerCase())) ||
        r.state === filter) &&
      [r.id, r.team_id, r.resource_id, r.kind]
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  const tasksByState = [...new Set(recent.map((r) => r.state))];
  if (source === "sandbox")
    return <PersistentSandboxExecution refreshKey={refreshKey} />;
  return (
    <>
      <ManagementPanel title={t("业务执行记录", "Execution records")}>
        <div className="monitor-toolbar">
          <label>
            {t("执行类型", "Source")}
            <select
              value={source}
              onChange={(e) => update("execution_source", e.target.value)}
            >
              <option value="sandbox">
                {t("Sandbox 执行记录", "Sandbox execution records")}
              </option>
              <option value="registrations">
                {t("Team 创建 / 建库", "Team provisioning")}
              </option>
              <option value="publish">
                {t("应用发布", "Application publishing")}
              </option>
              <option value="operations">
                {t("管理操作 / 迁移", "Management operations")}
              </option>
              <option value="task">
                {t(
                  "Task / AgentTask 业务统计",
                  "Task / AgentTask business statistics",
                )}
              </option>
              <option value="conversation">
                {t("对话回复 / 模型调用", "Replies / model calls")}
              </option>
            </select>
          </label>
          <label>
            {t("创建时间范围", "Created within")}
            <select
              value={hours}
              onChange={(e) => update("hours", e.target.value)}
            >
              <option value={1}>{t("最近1小时", "Last hour")}</option>
              <option value={24}>{t("最近24小时", "Last 24 hours")}</option>
              <option value={168}>{t("最近7天", "Last 7 days")}</option>
              <option value={0}>
                {t("全部已读取记录", "All retrieved records")}
              </option>
            </select>
          </label>
          <label>
            {t("执行或Team", "Execution or Team")}
            <input
              value={search}
              onChange={(e) => update("search", e.target.value)}
              placeholder={t("ID / Team / 类型", "ID / Team / kind")}
            />
          </label>
          <label>
            {t("结果", "Result")}
            <select
              value={filter}
              onChange={(e) => update("result", e.target.value)}
            >
              <option value="all">{t("全部", "All")}</option>
              <option value="errors">
                {t("失败 / 超时", "Failed / timed out")}
              </option>
              {tasksByState.map((s) => (
                <option key={s} value={s}>
                  {stateLabel(s, t)}
                </option>
              ))}
            </select>
          </label>
        </div>
        {!supported ? (
          <Empty>
            {t(
              "此类业务聚合尚未接入。Sandbox 底层执行记录不能代替 Task 数量、对话成功率或模型响应时间。",
              "This business aggregation is not connected. Sandbox records do not measure Task totals, reply success rates, or model latency.",
            )}
          </Empty>
        ) : (
          <>
            <Note>
              {source === "sandbox"
                ? t(
                    "覆盖最近创建的最多20个现存 Sandbox，每个最多100条记录；不包含已回收环境的历史执行，下面仅统计已读取记录。",
                    "Covers up to 20 existing Sandboxes and 100 records each. Reclaimed-environment history is excluded; counts describe only retrieved records.",
                  )
                : t(
                    "展示管理接口返回的最近记录；筛选后的数量不代表完整周期总量。",
                    "Recent records returned by the owning management API; filtered counts are not complete period totals.",
                  )}
            </Note>
            {snapshot?.warnings.length ? (
              <Note>
                {t(
                  "部分来源读取失败或达到读取上限，当前覆盖不完整。",
                  "Some sources failed or reached a read limit; coverage is incomplete.",
                )}
              </Note>
            ) : null}
            <Summary
              items={[
                {
                  label: t(
                    "读取记录中尚在进行",
                    "In progress in retrieved records",
                  ),
                  value: usable ? String(live.length) : "—",
                  note: t("不按创建时间筛选", "Not filtered by creation time"),
                },
                {
                  label: t("区间创建记录", "Records created in window"),
                  value: usable ? String(recent.length) : "—",
                },
                {
                  label: t("其中已成功", "Succeeded among these"),
                  value: usable ? String(succeeded) : "—",
                },
                {
                  label: t("其中失败 / 超时", "Failed / timed out among these"),
                  value: usable ? String(errored) : "—",
                },
              ]}
            />
            {loading && !snapshot ? (
              <Empty>
                {t("正在读取执行记录…", "Loading execution records…")}
              </Empty>
            ) : !usable ? (
              <Empty>
                {t(
                  "执行记录数据源不可用或已过期。",
                  "Execution source unavailable or stale.",
                )}
              </Empty>
            ) : !items.length ? (
              <Empty>
                {t(
                  "所选范围内没有匹配记录。",
                  "No matching records in this range.",
                )}
              </Empty>
            ) : (
              <>
                <Table
                  headers={[
                    t("执行记录", "Execution"),
                    "Team",
                    t("状态", "State"),
                    t("创建时间", "Created"),
                    t("完成耗时", "Time to completion"),
                    t("操作", "Actions"),
                  ]}
                >
                  {items.slice(offset, offset + 20).map((item) => (
                    <tr key={`${item.resource_id}/${item.id}`}>
                      <td>
                        <strong>{item.id}</strong>
                        <span>{item.kind}</span>
                      </td>
                      <td>{item.team_id || "—"}</td>
                      <td>
                        <span
                          className={
                            failures.has(item.state) ? "monitor-error-text" : ""
                          }
                        >
                          {stateLabel(item.state, t)}
                        </span>
                        {item.error_code ? (
                          <span>{item.error_code}</span>
                        ) : null}
                      </td>
                      <td>{date(item.created_at)}</td>
                      <td>
                        {item.completed_at
                          ? duration(
                              (Date.parse(item.completed_at) -
                                Date.parse(item.created_at)) /
                                1000,
                            )
                          : "—"}
                      </td>
                      <td>
                        <button
                          className="secondary-button"
                          onClick={() => setSelected(item)}
                        >
                          {t("详情", "Details")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </Table>
                <ManagementPagination
                  total={items.length}
                  offset={offset}
                  limit={20}
                  onChange={setOffset}
                />
              </>
            )}
          </>
        )}
      </ManagementPanel>
      {fresh(data.placement, now) ? (
        <ManagementPanel
          title={t("当前 Team 路由异常", "Current Team routing issues")}
        >
          {data.placement.data?.placements.some((p) => !p.route_eligible) ? (
            <Table
              headers={[
                "Team",
                t("归属实例", "Owner"),
                t("状态", "State"),
                t("租约 / 心跳", "Lease / heartbeat"),
              ]}
            >
              {data.placement.data.placements
                .filter((p) => !p.route_eligible)
                .map((p) => (
                  <tr key={p.placement.team_id}>
                    <td>{p.placement.team_id}</td>
                    <td>{p.placement.owner_instance_id || "—"}</td>
                    <td>{p.placement.placement_state}</td>
                    <td>
                      {p.owner_lease_fresh
                        ? t("租约有效", "Lease valid")
                        : t("租约失效", "Lease expired")}{" "}
                      /{" "}
                      {p.owner_heartbeat_fresh
                        ? t("心跳有效", "Heartbeat fresh")
                        : t("心跳过期", "Heartbeat stale")}
                    </td>
                  </tr>
                ))}
            </Table>
          ) : (
            <Empty>
              {t(
                "没有不可路由的已登记 Team。",
                "No unroutable registered Teams.",
              )}
            </Empty>
          )}
        </ManagementPanel>
      ) : null}
      {selected ? (
        <ManagementDialog
          title={t("执行详情", "Execution details")}
          onClose={() => setSelected(null)}
        >
          <dl className="monitor-detail">
            {Object.entries({
              ID: selected.id,
              Team: selected.team_id,
              [t("资源", "Resource")]: selected.resource_id,
              [t("类型", "Kind")]: selected.kind,
              [t("状态", "State")]: stateLabel(selected.state, t),
              [t("创建", "Created")]: date(selected.created_at),
              [t("更新", "Updated")]: date(selected.updated_at),
              [t("完成", "Completed")]: date(selected.completed_at),
              [t("错误代码", "Error code")]: selected.error_code,
              [t("操作者", "Actor")]: selected.actor,
              [t("操作原因", "Reason")]: selected.reason,
            }).map(([key, value]) => (
              <div key={key}>
                <dt>{key}</dt>
                <dd>{value || "—"}</dd>
              </div>
            ))}
          </dl>
          <button
            className="secondary-button"
            onClick={() => setSelected(null)}
          >
            {t("关闭", "Close")}
          </button>
        </ManagementDialog>
      ) : null}
    </>
  );
}
